import { useAtomValue } from 'jotai';
import { useFormContext } from 'react-hook-form';

import { Grid, Stack } from '@crossnokaye/ux-common';

import { DisplayRefDisplayType } from '@repo/sdk/models/components';

import { settingsInputMap } from '@/features/equipment-browser/equipment-details/settings/maps/settings-inputs.map';
import { equipmentLoadingAtom } from '@/features/equipment-browser/equipment-details/state/equipment-response.state';
import { activeSequencerAtom } from '@/features/equipment-browser/equipment-details/state/sequencers.state';
import { SubSectionTemplate } from '@/features/equipment-browser/equipment-details/templates/subsection-template';
import { EnhancedSetting } from '@/features/equipment-browser/subscriptions/use-get-settings-values';

import { SettingFieldButton } from './setting-field-button';

interface SequencerSettingsProps {
  settings: EnhancedSetting[];
}

export const SequencerSettings = ({ settings }: SequencerSettingsProps): React.ReactNode => {
  const form = useFormContext();
  const isLoading = useAtomValue(equipmentLoadingAtom);
  const activeSequencer = useAtomValue(activeSequencerAtom);

  // split settings to pull all buttons out to a separate array
  const buttons = settings.filter(
    (setting: EnhancedSetting) => setting.displayType === DisplayRefDisplayType.Button
  );
  const otherSettings = settings.filter(
    (setting: EnhancedSetting) => setting.displayType !== DisplayRefDisplayType.Button
  );

  return (
    <SubSectionTemplate
      title={`Sequencer (${activeSequencer?.displayName ?? 'Unknown Name'})`}
      isLoading={isLoading}
    >
      {otherSettings?.length > 0 && (
        <Grid variant="responsive-5" className="p-gutter-sm @xl:p-gutter">
          {otherSettings?.map((setting: EnhancedSetting) => {
            const SettingInput =
              settingsInputMap[setting.displayType ?? DisplayRefDisplayType.Input];

            return (
              <Stack key={`sequencer-settings-input-${setting.ref.id}`} gap="sm">
                <SettingInput
                  form={form}
                  setting={setting}
                  name={`sequencerSettings.${setting.ref.id}`}
                />
              </Stack>
            );
          })}
        </Grid>
      )}
      {buttons?.length > 0 && (
        <Grid variant="responsive-5" className="p-gutter-sm @xl:p-gutter">
          {buttons?.map((button: EnhancedSetting) => (
            <SettingFieldButton
              key={`sequencer-settings-button-${button.ref.id}`}
              setting={button}
            />
          ))}
        </Grid>
      )}
    </SubSectionTemplate>
  );
};
